/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Box, Grid, LinearProgress, Typography, Card, CardContent, Skeleton, Alert } from "@mui/material";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import PendingIcon from "@mui/icons-material/Pending";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import { toast } from "react-toastify";
import { PageHeader, StatsCard, Card as SharedCard, TouchFriendlyTooltip } from "../shared";
import { IconButton, Avatar, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { PageViewsChart, PerformanceGraphChart } from "../shared/Charts";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RemoveIcon from "@mui/icons-material/Remove";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PostAddIcon from "@mui/icons-material/PostAdd";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useTheme } from "@mui/material/styles";
import {
  apiFlagIsTrue,
  countMasterOrgMembersFromResponse,
  shouldShowCommunityJoinRequests,
} from "../../Utils/func";

const DashboardHome = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [subOrgsData, setSubOrgsData] = useState([]);
  const token = props?.props?.profile?.token;
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const canInviteClubsRaw = props?.props?.profile?.can_invite_clubs;
  const canInviteClubs =
    canInviteClubsRaw == null ? true : apiFlagIsTrue(canInviteClubsRaw);
  const showCommunityJoinRequests = shouldShowCommunityJoinRequests(
    props?.props?.profile,
    analytics
  );

  const loadAnalytics = async () => {
    if (!token) return;
    setAnalyticsLoading(true);
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    
    try {
      // Use first sport from profile or default to 1 for analytics
      const sport_id = props?.props?.profile?.sportList?.[0]?.id || 
                       props?.props?.profile?.sportIdList?.[0] || 
                       1;
      
      // Pass sport_id to analytics endpoint
      const analyticsUrl = `${ApiEndpoint.MASTER_ORG_ANALYTICS_DASHBOARD}${sport_id ? `?sport_id=${sport_id}` : ''}`;
      const [analyticsData, subOrgsResponse, usersResponse] = await Promise.all([
        ApiServices.GetApiCall(analyticsUrl, headers),
        ApiServices.GetApiCall(ApiEndpoint.MASTER_ORG_SUB_ORGS, headers),
        ApiServices.PostApiCall(
          ApiEndpoint.MASTER_ORG_USERS,
          JSON.stringify({ type: "user", pagination: false, status: "active" }),
          headers
        ),
      ]);

      const subOrgs = subOrgsResponse?.status ? subOrgsResponse.data || [] : [];
      setSubOrgsData(subOrgs);

      let resolvedAnalytics = analyticsData?.status ? analyticsData.data || {} : {};

      // Members: use the same users list as the Members page (not dashboard aggregate).
      if (usersResponse?.status) {
        const { total, active } = countMasterOrgMembersFromResponse(usersResponse);
        resolvedAnalytics = { ...resolvedAnalytics, total_members: total, active_members: active };
      }

      setAnalyticsLoading(false);
      if (analyticsData?.status || usersResponse?.status) {
        setAnalytics(resolvedAnalytics);
      }
    } catch (error) {
      setAnalyticsLoading(false);
      toast.error("Failed to load analytics");
    }
  };

  useEffect(() => {
    if (userType === "master_org" && token) {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, token]);

  if (userType !== "master_org") {
    return (
      <Box>
        <PageHeader title="Dashboard" subtitle="Access denied." />
        <SharedCard>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            This page is for club partner accounts only.
          </Typography>
        </SharedCard>
      </Box>
    );
  }

  // Sub-org metrics
  const totalSubOrgs = analytics?.total_sub_orgs ?? subOrgsData.length ?? 0;
  const maxSubOrgs = analytics?.max_sub_orgs ?? 0;
  const usagePercent =
    maxSubOrgs > 0 ? Math.min((totalSubOrgs / maxSubOrgs) * 100, 100) : 0;
  
  const activePosts = analytics?.active_posts ?? 0;

  // Members metrics (active only — matches org website)
  const activeMembers = analytics?.active_members ?? analytics?.total_members ?? 0;
  
  // Connection requests metrics
  const totalConnectionRequests = analytics?.total_connection_requests ?? 0;
  const pendingConnectionRequests = analytics?.pending_connection_requests ?? 0;
  const approvedConnectionRequests = analytics?.approved_connection_requests ?? 0;
  const recentConnectionRequests = analytics?.recent_connection_requests ?? 0;
  
  // Status breakdown - use API data if available, otherwise calculate from subOrgsData
  // Transform to PageViewsChart format: { month: string, views: number }
  const statusBreakdown = analytics?.sub_org_status_breakdown;
  const distributionData = statusBreakdown ? [
    {
      month: "Approved",
      views: statusBreakdown.approved || 0,
    },
    {
      month: "Pending",
      views: statusBreakdown.pending || 0,
    },
    {
      month: "Denied",
      views: statusBreakdown.denied || 0,
    },
  ] : [
    {
      month: "Active",
      views: subOrgsData.filter((org) => org.status === "approved" || org.status === "active" || org.connection_status === "approved").length,
    },
    {
      month: "Pending",
      views: subOrgsData.filter((org) => org.status === "pending" || org.connection_status === "pending").length,
    },
    {
      month: "Inactive",
      views: subOrgsData.filter((org) => org.status === "denied" || org.status === "inactive" || org.connection_status === "denied").length,
    },
  ];

  // Focal metrics for club partners (most followers, highest engagement, most active)
  const topPerformingSubOrgs = analytics?.top_performing_sub_orgs || analytics?.topPerformingSubOrgs || [];
  const performanceGraphData = analytics?.performance_graph_data || analytics?.performanceGraphData || [];
  const mostActiveSubOrgs = analytics?.most_active_sub_orgs || analytics?.mostActiveSubOrgs || [];
  const highestEngagementSubOrgs = analytics?.highest_engagement_sub_orgs || analytics?.highestEngagementSubOrgs || [];
  const mostFollowersSubOrgs = analytics?.most_followers_sub_orgs || analytics?.mostFollowersSubOrgs || [];

  // Helper function to get trend icon
  const getTrendIcon = (trend) => {
    if (trend === "increasing") {
      return <TrendingUpIcon sx={{ fontSize: "0.875rem", color: "#10b981" }} />;
    } else if (trend === "decreasing") {
      return <TrendingDownIcon sx={{ fontSize: "0.875rem", color: "#ef4444" }} />;
    }
    return <RemoveIcon sx={{ fontSize: "0.875rem", color: isDark ? "#718096" : "#718096" }} />;
  };

  const connectedClubName =
    subOrgsData[0]?.name ||
    subOrgsData[0]?.organisation_name ||
    subOrgsData[0]?.club_name ||
    "";

  return (
    <Box sx={{ px: { xs: 1, sm: 0 }, pb: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Dashboard"
        subtitle="Overview of your club partner and connected clubs"
        breadcrumbs={[{ label: "Club Partner" }, { label: "Dashboard" }]}
      >
        <TouchFriendlyTooltip
          title={
            <Box sx={{ maxWidth: 320 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                Master club overview
              </Typography>
              {canInviteClubs ? (
                <>
                  <Typography variant="body2">
                    1. Create invite codes for clubs on the <strong>Invite Codes</strong> page.
                  </Typography>
                  {showCommunityJoinRequests && (
                    <>
                      <Typography variant="body2">
                        2. Clubs sign up with a code and appear in <strong>Join Requests</strong>.
                      </Typography>
                      <Typography variant="body2">
                        3. Approve requests to move clubs into <strong>Clubs</strong>.
                      </Typography>
                    </>
                  )}
                  {!showCommunityJoinRequests && (
                    <Typography variant="body2">
                      2. Clubs that use your invite codes are connected automatically and appear in <strong>Clubs</strong>.
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2">
                  You are connected to a club via a CLB- invite. Use <strong>Connect with Club</strong> if another club sends you an invite code.
                </Typography>
              )}
              <Typography variant="body2">
                Members who follow or interact with your clubs appear on the
                <strong> Members</strong> page.
              </Typography>
            </Box>
          }
          arrow
        >
          <IconButton
            size="small"
            sx={{ color: isDark ? "#cbd5e1" : "#4a5568" }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </TouchFriendlyTooltip>
      </PageHeader>

      {!canInviteClubs && (
        <Alert
          severity="info"
          sx={{
            mb: { xs: 2, md: 3 },
            borderRadius: "12px",
            backgroundColor: isDark ? "rgba(59, 130, 246, 0.1)" : "#eff6ff",
            color: isDark ? "#bfdbfe" : "#1e40af",
            "& .MuiAlert-icon": { color: isDark ? "#93c5fd" : "#2563eb" },
          }}
        >
          {connectedClubName
            ? `You're connected to ${connectedClubName}. You can't invite other clubs unless promoted by an admin.`
            : "You're connected to a club. You can't invite other clubs unless promoted by an admin."}
        </Alert>
      )}

      {/* Row 1: 4 Cards stacked Left | Focal (Most Followers / Engagement / Active) Right */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 }, alignItems: "stretch" }}>
        {analyticsLoading ? (
          <>
            <Grid item xs={6} lg={2.5}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 3 }, width: "100%", height: "100%" }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Skeleton variant="rectangular" height={130} sx={{ borderRadius: 2 }} />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Skeleton variant="rectangular" height={130} sx={{ borderRadius: 2 }} />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} lg={2.5}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 3 }, width: "100%", height: "100%" }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Skeleton variant="rectangular" height={130} sx={{ borderRadius: 2 }} />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Skeleton variant="rectangular" height={130} sx={{ borderRadius: 2 }} />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} lg={7}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Grid>
          </>
        ) : (
          <>
            {/* Left: 4 Stats Cards — 2×2 grid on all breakpoints */}
            <Grid item xs={6} lg={2.5} sx={{ display: "flex" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 3 }, width: "100%", height: "100%" }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <StatsCard
                    title="Clubs"
                    value={totalSubOrgs}
                    subtitle={`of ${maxSubOrgs} allowed`}
                    icon={CorporateFareIcon}
                    color="primary"
                  />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <StatsCard
                    title="Active Members"
                    value={activeMembers.toLocaleString()}
                    subtitle="across connected clubs"
                    icon={PeopleIcon}
                    color="primary"
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} lg={2.5} sx={{ display: "flex" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 3 }, width: "100%", height: "100%" }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <StatsCard
                    title="Capacity Used"
                    value={`${usagePercent.toFixed(0)}%`}
                    subtitle="of total capacity"
                    icon={TrendingUpIcon}
                    color={usagePercent > 80 ? "danger" : "success"}
                  />
                </Box>
                {showCommunityJoinRequests && (
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <StatsCard
                      title="Pending Requests"
                      value={pendingConnectionRequests.toLocaleString()}
                      subtitle={`${recentConnectionRequests} recent`}
                      icon={PendingIcon}
                      color={pendingConnectionRequests > 0 ? "error" : "warning"}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            {/* Focal: Most Followers | Highest Engagement | Most Active */}
            <Grid item xs={12} lg={7} sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 0 } }}>
              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ height: "100%" }}>
                {/* Most Followers */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                      border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
                      boxShadow: isDark ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "0 4px 12px rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 1, md: 2 } }}>
                        <PeopleIcon sx={{ color: isDark ? "#34d399" : "#10b981", fontSize: isMobile ? "1.25rem" : "1.5rem" }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: isMobile ? "0.875rem" : "1rem", fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c" }}>
                            Most Followers
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#cbd5e1" : "#718096" }}>
                            Total and active followers
                          </Typography>
                        </Box>
                      </Box>
                      {analyticsLoading ? (
                        <Box>
                          {[1, 2, 3].map((i) => (
                            <Skeleton key={i} variant="rectangular" height={isMobile ? 44 : 52} sx={{ borderRadius: 1, mb: 1 }} />
                          ))}
                        </Box>
                      ) : mostFollowersSubOrgs.length > 0 ? (
                        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                          <Table size="small">
                            <TableBody>
                              {mostFollowersSubOrgs.slice(0, isMobile ? 3 : 5).map((subOrg, index) => (
                                <TableRow key={subOrg.sub_org_id || index}>
                                  <TableCell sx={{ border: "none", py: isMobile ? 0.75 : 1.25 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 1.5 }}>
                                      <Avatar
                                        src={subOrg.logo_url}
                                        sx={{
                                          width: isMobile ? 28 : 32,
                                          height: isMobile ? 28 : 32,
                                          bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                                        }}
                                      >
                                        {(subOrg.sub_org_name?.[0] || "O").toUpperCase()}
                                      </Avatar>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: isMobile ? "0.75rem" : "0.8125rem", fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                          {subOrg.sub_org_name || "Unknown"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#cbd5e1" : "#718096" }}>
                                          {subOrg.total_followers?.toLocaleString() || 0} total • {subOrg.active_followers?.toLocaleString() || 0} active
                                        </Typography>
                                      </Box>
                                      {(subOrg.follower_growth_rate > 0 || subOrg.follower_trend) && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                          {getTrendIcon(subOrg.follower_trend)}
                                          {subOrg.follower_growth_rate > 0 && (
                                            <Typography sx={{ fontSize: "0.7rem", color: "#10b981", fontWeight: 600 }}>
                                              +{subOrg.follower_growth_rate?.toFixed(1) || 0}%
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem", textAlign: "center", py: 2 }}>
                          No data available
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                {/* Highest Engagement */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                      border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
                      boxShadow: isDark ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "0 4px 12px rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 1, md: 2 } }}>
                        <FavoriteIcon sx={{ color: isDark ? "#f472b6" : "#ec4899", fontSize: isMobile ? "1.25rem" : "1.5rem" }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: isMobile ? "0.875rem" : "1rem", fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c" }}>
                            Highest Engagement
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#cbd5e1" : "#718096" }}>
                            Likes, saves, views
                          </Typography>
                        </Box>
                      </Box>
                      {analyticsLoading ? (
                        <Box>
                          {[1, 2, 3].map((i) => (
                            <Skeleton key={i} variant="rectangular" height={isMobile ? 44 : 52} sx={{ borderRadius: 1, mb: 1 }} />
                          ))}
                        </Box>
                      ) : highestEngagementSubOrgs.length > 0 ? (
                        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                          <Table size="small">
                            <TableBody>
                              {highestEngagementSubOrgs.slice(0, isMobile ? 3 : 5).map((subOrg, index) => (
                                <TableRow key={subOrg.sub_org_id || index}>
                                  <TableCell sx={{ border: "none", py: isMobile ? 0.75 : 1.25 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 1.5 }}>
                                      <Avatar
                                        src={subOrg.logo_url}
                                        sx={{
                                          width: isMobile ? 28 : 32,
                                          height: isMobile ? 28 : 32,
                                          bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                                        }}
                                      >
                                        {(subOrg.sub_org_name?.[0] || "O").toUpperCase()}
                                      </Avatar>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: isMobile ? "0.75rem" : "0.8125rem", fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                          {subOrg.sub_org_name || "Unknown"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#cbd5e1" : "#718096" }}>
                                          {subOrg.total_engagement?.toLocaleString() || 0} total • {subOrg.engagement_rate?.toFixed(1) || 0}% rate
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem", textAlign: "center", py: 2 }}>
                          No data available
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                {/* Most Active */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                      border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
                      boxShadow: isDark ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "0 4px 12px rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 1, md: 2 } }}>
                        <PostAddIcon sx={{ color: isDark ? "#818cf8" : "#1976d2", fontSize: isMobile ? "1.25rem" : "1.5rem" }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: isMobile ? "0.875rem" : "1rem", fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c" }}>
                            Most Active
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#cbd5e1" : "#718096" }}>
                            Posts per week/month
                          </Typography>
                        </Box>
                      </Box>
                      {analyticsLoading ? (
                        <Box>
                          {[1, 2, 3].map((i) => (
                            <Skeleton key={i} variant="rectangular" height={isMobile ? 44 : 52} sx={{ borderRadius: 1, mb: 1 }} />
                          ))}
                        </Box>
                      ) : mostActiveSubOrgs.length > 0 ? (
                        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                          <Table size="small">
                            <TableBody>
                              {mostActiveSubOrgs.slice(0, isMobile ? 3 : 5).map((subOrg, index) => (
                                <TableRow key={subOrg.sub_org_id || index}>
                                  <TableCell sx={{ border: "none", py: isMobile ? 0.75 : 1.25 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 1.5 }}>
                                      <Avatar
                                        src={subOrg.logo_url}
                                        sx={{
                                          width: isMobile ? 28 : 32,
                                          height: isMobile ? 28 : 32,
                                          bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                                        }}
                                      >
                                        {(subOrg.sub_org_name?.[0] || "O").toUpperCase()}
                                      </Avatar>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: isMobile ? "0.75rem" : "0.8125rem", fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                          {subOrg.sub_org_name || "Unknown"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#cbd5e1" : "#718096" }}>
                                          {subOrg.posts_last_30_days || 0} posts (30d)
                                        </Typography>
                                      </Box>
                                      {getTrendIcon(subOrg.activity_trend)}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem", textAlign: "center", py: 2 }}>
                          No data available
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>

      {/* Row 2: Top Performers Summary Cards */}
      {(analyticsLoading || topPerformingSubOrgs.length > 0) && (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
          <Grid item xs={12}>
            <Card
              sx={{
                backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
                boxShadow: isDark
                  ? "0 4px 12px rgba(0, 0, 0, 0.5)"
                  : "0 4px 12px rgba(0, 0, 0, 0.08)",
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  sx={{
                    fontSize: isMobile ? "1rem" : "1.125rem",
                    fontWeight: 600,
                    color: isDark ? "#ffffff" : "#1a202c",
                    mb: 2,
                  }}
                >
                  Top Performers Summary
                </Typography>
                {analyticsLoading ? (
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} variant="rectangular" width={isMobile ? "100%" : 200} height={isMobile ? 90 : 100} sx={{ borderRadius: 2 }} />
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 2, flexWrap: "wrap" }}>
                    {topPerformingSubOrgs.slice(0, 3).map((subOrg, index) => (
                      <Card
                        key={subOrg.sub_org_id || index}
                        sx={{
                          flex: isMobile ? "none" : "1 1 200px",
                          minWidth: isMobile ? 0 : 200,
                          width: isMobile ? "100%" : undefined,
                          backgroundColor: isDark ? "#242424" : "#f7fafc",
                          border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
                        }}
                      >
                        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 1.5, mb: 2 }}>
                            <Avatar
                              src={subOrg.logo_url}
                              sx={{
                                width: isMobile ? 36 : 40,
                                height: isMobile ? 36 : 40,
                                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                              }}
                            >
                              {(subOrg.sub_org_name?.[0] || "O").toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: isMobile ? "0.8125rem" : "0.875rem",
                                  fontWeight: 600,
                                  color: isDark ? "#ffffff" : "#1a202c",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {subOrg.sub_org_name || "Unknown"}
                              </Typography>
                              <TouchFriendlyTooltip
                                title={
                                  <Box>
                                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                      Performance Index: {subOrg.performance_score?.toFixed(1) || 0}
                                    </Typography>
                                    {subOrg.score_breakdown ? (
                                      <>
                                        <Typography variant="body2" sx={{ mb: 0.5, mt: 1 }}>
                                          Score Breakdown:
                                        </Typography>
                                        <Typography variant="body2">• Likes: {subOrg.score_breakdown.likes_score?.toFixed(1) || 0} (35% weight)</Typography>
                                        <Typography variant="body2">• Saves: {subOrg.score_breakdown.saves_score?.toFixed(1) || 0} (25% weight)</Typography>
                                        <Typography variant="body2">• Views: {subOrg.score_breakdown.views_score?.toFixed(1) || 0} (25% weight)</Typography>
                                        <Typography variant="body2">• Posts: {subOrg.score_breakdown.posts_score?.toFixed(1) || 0} (15% weight)</Typography>
                                        <Typography variant="body2" sx={{ mt: 1, fontSize: "0.7rem", opacity: 0.9 }}>
                                          Each metric normalized to 0-100 scale
                                        </Typography>
                                      </>
                                    ) : (
                                      <>
                                        <Typography variant="body2" sx={{ mb: 0.5, mt: 1 }}>
                                          Calculated from:
                                        </Typography>
                                        <Typography variant="body2">• Likes: 35% weight</Typography>
                                        <Typography variant="body2">• Saves: 25% weight</Typography>
                                        <Typography variant="body2">• Views: 25% weight</Typography>
                                        <Typography variant="body2">• Posts: 15% weight</Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, fontSize: "0.7rem", opacity: 0.9 }}>
                                          Normalized to 0-100 scale
                                        </Typography>
                                      </>
                                    )}
                                  </Box>
                                }
                                arrow
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.75rem",
                                      color: isDark ? "#cbd5e1" : "#718096",
                                      cursor: "help",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        fontWeight: 600,
                                        color: isDark ? "#818cf8" : "#1976d2",
                                      }}
                                    >
                                      Performance Index:
                                    </Box>
                                    {subOrg.performance_score?.toFixed(1) || 0}
                                  </Typography>
                                </Box>
                              </TouchFriendlyTooltip>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Chip
                              icon={<ThumbUpIcon sx={{ fontSize: isMobile ? "0.75rem !important" : "0.875rem !important" }} />}
                              label={`${(subOrg.total_likes || 0).toLocaleString()} likes`}
                              size="small"
                              sx={{
                                fontSize: isMobile ? "0.65rem" : "0.7rem",
                                height: isMobile ? 22 : 24,
                                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                                color: isDark ? "#818cf8" : "#1976d2",
                              }}
                            />
                            <Chip
                              icon={<BookmarkIcon sx={{ fontSize: isMobile ? "0.75rem !important" : "0.875rem !important" }} />}
                              label={`${(subOrg.total_saves || 0).toLocaleString()} saves`}
                              size="small"
                              sx={{
                                fontSize: isMobile ? "0.65rem" : "0.7rem",
                                height: isMobile ? 22 : 24,
                                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                                color: isDark ? "#818cf8" : "#1976d2",
                              }}
                            />
                            <Chip
                              icon={<VisibilityIcon sx={{ fontSize: isMobile ? "0.75rem !important" : "0.875rem !important" }} />}
                              label={`${(subOrg.total_views || 0).toLocaleString()} views`}
                              size="small"
                              sx={{
                                fontSize: isMobile ? "0.65rem" : "0.7rem",
                                height: isMobile ? 22 : 24,
                                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                                color: isDark ? "#818cf8" : "#1976d2",
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Row 3: Top Performing chart | Active Members/Posts | Join Requests (optional) */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 }, alignItems: "stretch" }}>
        {analyticsLoading ? (
          <>
            <Grid item xs={12} lg={7}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={6} lg={2.5}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 3 }, width: "100%", height: "100%" }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Skeleton variant="rectangular" height={130} sx={{ borderRadius: 2 }} />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Skeleton variant="rectangular" height={130} sx={{ borderRadius: 2 }} />
                </Box>
              </Box>
            </Grid>
            {showCommunityJoinRequests && (
              <Grid item xs={6} lg={2.5}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 3 }, width: "100%", height: "100%" }}>
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <Skeleton variant="rectangular" height={130} sx={{ borderRadius: 2 }} />
                  </Box>
                </Box>
              </Grid>
            )}
          </>
        ) : (
          <>
            {/* Left: Top Performing Sub-Organizations Chart (7 columns) */}
            <Grid item xs={12} lg={7}>
              <Card
                sx={{
                  height: "100%",
                  backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                  border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
                  boxShadow: isDark
                    ? "0 4px 12px rgba(0, 0, 0, 0.5)"
                    : "0 4px 12px rgba(0, 0, 0, 0.08)",
                }}
              >
                <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column", p: { xs: 2, md: 3 }, pb: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", gap: 1, mb: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: isMobile ? "1rem" : "1.125rem",
                          fontWeight: 600,
                          color: isDark ? "#ffffff" : "#1a202c",
                          mb: 0.5,
                        }}
                      >
                        Top Performing Clubs
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: isMobile ? "0.8rem" : "0.875rem",
                          color: isDark ? "#cbd5e1" : "#718096",
                        }}
                      >
                        Performance based on likes, saves, views, and posts
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: isMobile ? "left" : "right", flexShrink: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: 700,
                          color: isDark ? "#ffffff" : "#1a202c",
                        }}
                      >
                        {topPerformingSubOrgs.length}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: isDark ? "#9e9e9e" : "#757575",
                          mt: 0.5,
                        }}
                      >
                        Top performers
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, ml: isMobile ? 0 : -2, mr: isMobile ? 0 : -1, height: isMobile ? 260 : 300 }}>
                    {performanceGraphData.length > 0 ? (
                      <PerformanceGraphChart data={performanceGraphData} height={isMobile ? 240 : 300} />
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: isMobile ? 220 : 300,
                        color: isDark ? '#718096' : '#718096',
                        fontSize: isMobile ? '0.8rem' : '0.875rem'
                      }}>
                        No performance data available
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            {/* Right: 4 Stats Cards — 2×2 grid on all breakpoints */}
            <Grid item xs={6} lg={2.5} sx={{ display: "flex" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 3 }, width: "100%", height: "100%" }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <StatsCard
                    title="Active Posts"
                    value={activePosts.toLocaleString()}
                    subtitle="Currently live"
                    icon={CheckCircleIcon}
                    color="success"
                  />
                </Box>
              </Box>
            </Grid>
            {showCommunityJoinRequests && (
              <Grid item xs={6} lg={2.5} sx={{ display: "flex" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 3 }, width: "100%", height: "100%" }}>
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <StatsCard
                      title="Join Requests"
                      value={totalConnectionRequests.toLocaleString()}
                      subtitle={`${approvedConnectionRequests} approved`}
                      icon={GroupAddIcon}
                      color="warning"
                    />
                  </Box>
                </Box>
              </Grid>
            )}
          </>
        )}
      </Grid>

      {/* Usage Card */}
      <SharedCard title="Club Usage" subtitle="Track your club capacity">
        <Box sx={{ mt: { xs: 1.5, md: 2 }, px: { xs: 0, sm: 0 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
            <Typography sx={{ fontSize: isMobile ? "0.8125rem" : "0.875rem", fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c" }}>
              {totalSubOrgs} / {maxSubOrgs} Clubs
            </Typography>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: usagePercent > 80 ? "#ef4444" : "#2563eb",
              }}
            >
              {usagePercent.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={usagePercent}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: isDark ? "#242424" : "#e2e8f0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: usagePercent > 80 ? "#ef4444" : "#2563eb",
                borderRadius: 6,
              },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#718096" }}>
              {maxSubOrgs - totalSubOrgs} slots remaining
            </Typography>
            {usagePercent > 80 && (
              <Typography sx={{ fontSize: "0.75rem", color: "#ef4444", fontWeight: 500 }}>
                Approaching capacity limit
              </Typography>
            )}
          </Box>
        </Box>
      </SharedCard>
    </Box>
  );
};

export default DashboardHome;
